import type {
  FareSliceDetailsV2,
  TripSlice,
} from "@b2bportal/air-shopping-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getDurationFromMins } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { MixedCabinPill } from "@tenant/flights";
import { AirlineIcon, Icon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./styles.module.scss";

export interface ItinerarySummaryComponentProps extends PlatformComponentProps {
  tripSlice: TripSlice;
  fareSlice: FareSliceDetailsV2;
  hideBorder?: boolean;
}

export const ItinerarySummary = ({
  tripSlice,
  fareSlice,
  className,
  hideBorder,
}: ItinerarySummaryComponentProps) => {
  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.ItinerarySummary
  );
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const duration = getDurationFromMins(tripSlice.totalDurationMinutes);
  const durationString = duration
    .filter((unit) => unit.value)
    .map((unit) => t(`flightDuration.${unit.key}`, { count: unit.value }))
    .join(" ")
    .trim();

  const departureTime = formatDateTime(
    dayjs(removeTimezone(tripSlice.departureTime)).toDate(),
    DateTimeFormatStyle.ShortTime
  );
  const arrivalTime = formatDateTime(
    dayjs(removeTimezone(tripSlice.arrivalTime)).toDate(),
    DateTimeFormatStyle.ShortTime
  );
  const plusDays = dayjs(removeTimezone(tripSlice.arrivalTime))
    .startOf("day")
    .diff(dayjs(removeTimezone(tripSlice.departureTime)).startOf("day"), "day");
  const plusDaysLabel =
    plusDays > 0
      ? t("flightArrivalDifferentDay.plusDaysCount", {
          count: plusDays,
          defaultValue: `+${plusDays} day${plusDays === 1 ? "" : "s"}`,
        })
      : null;
  const arrivalDateLabel =
    plusDays !== 0
      ? formatDateTime(
          dayjs(removeTimezone(tripSlice.arrivalTime)).toDate(),
          DateTimeFormatStyle.MediumDate
        )
      : null;

  const brandName = fareSlice.fareShelf?.brandName ?? "";
  const primaryCarrier = tripSlice.segmentDetails[0].marketingAirline.code;
  const airlineName = tripSlice.segmentDetails[0].marketingAirline.name;
  const flightStops = tripSlice.segmentDetails.length - 1;

  return (
    <div
      className={clsx(block, className, { [cn("hide-border")]: hideBorder })}
    >
      <div className={cn("time-container")}>
        <span>{departureTime}</span>
        <Icon
          className={cn("airplane-icon")}
          iconName={IconNameEnum.airplane}
        />
        <span>{arrivalTime}</span>
        {plusDaysLabel != null ? (
          <span className={cn("plus-days")}>{plusDaysLabel}</span>
        ) : null}
      </div>

      {arrivalDateLabel != null ? (
        <div className={cn("arrives-on")}>
          <Trans
            i18nKey="flightArrivalDifferentDay.flightArrivesOnDate"
            values={{ arrivalDate: arrivalDateLabel }}
            components={{ 1: <span className={cn("notification-date")} /> }}
          />
        </div>
      ) : null}

      <div className={cn("airline")}>
        <AirlineIcon
          airlineCode={primaryCarrier}
          altText={airlineName}
          className={cn("airline-icon")}
        />
        {airlineName} - {brandName}
      </div>

      <div className={cn("duration")}>{durationString}</div>
      <div className={cn("stops")}>
        {flightStops === 0
          ? t("stopDetails.nonstop")
          : t("stopDetails.stop", { count: flightStops })}
      </div>
      <MixedCabinPill
        fareSlice={fareSlice}
        className={cn("mixed-cabin-pill")}
      />
    </div>
  );
};
